import Quill from 'quill';
import 'quill-emoji';
import ImageResize from 'quill-image-resize';
import 'quill-mention';

export class QuillConfiguration {

  private static initialized: boolean = false;

  private static MODULES_CONFIG = {
    'emoji-shortname': false,
    'emoji-textarea': false,
    'emoji-toolbar': true,
    imageResize: {
      modules: [
        'DisplaySize',
        'Resize',
      ]
    },
    keyboard: { bindings: { tab: false } },
    mention: {
      allowedChars: /^[A-Za-z!]*$/,
      isolateCharacter: true,
      showDenotationChar: false,
      mentionDenotationChars: ['!!!'],
      source: (searchTerm, renderList) => {

        const fields = [
          { id: 1, value: '!!!id!!!' },
          { id: 2, value: '!!!name!!!' },
          { id: 3, value: '!!!company!!!' },
        ];

        const matches = !!searchTerm.length
          ? fields.filter(field => field.value.includes(searchTerm))
          : fields;

        renderList(matches, searchTerm);
      },
    },
  };

  static get modules() {

    QuillConfiguration.initialize();

    return QuillConfiguration.MODULES_CONFIG;
  }

  static initialize() {

    if (QuillConfiguration.initialized) {
      return;
    }

    QuillConfiguration.configureFormattingOptions();
    QuillConfiguration.configureImageOptions();

    QuillConfiguration.initialized = true;
  }

  private static configureFormattingOptions() {

    const align = Quill.import('attributors/style/align');
    const background = Quill.import('attributors/style/background');
    const color = Quill.import('attributors/style/color');
    const font = Quill.import('attributors/style/font');
    const size = Quill.import('attributors/style/size');

    font.whitelist = [
      'sans-serif', 'monospace', 'serif',
      'arial', 'courier', 'georgia', 'helvetica', 'impact',
      'lucida', 'tahoma', 'times', 'trebuchet', 'verdana'
    ];

    Quill.register(align, true);
    Quill.register(background, true);
    Quill.register(color, true);
    Quill.register(font, true);
    Quill.register(size, true);
  }

  private static configureImageOptions() {

    Quill.register('modules/imageResize', ImageResize);

    const allowedImageAttributes = ['alt', 'height', 'width', 'style'];
    const maxInitialWidth = 782;

    const QuillImage = Quill.import('formats/image');

    class CustomImageFormat extends QuillImage {

      static constrainIfNoWidth(
        domNode: HTMLImageElement
      ) {
        setTimeout(() => {

          if (!domNode.getAttribute('width')?.length
            && domNode.naturalWidth > maxInitialWidth) {
            console.info('Limited editor image to ' + maxInitialWidth, domNode);
            domNode.setAttribute('width', String(maxInitialWidth));
          }
        }, 120);
      }

      static formats(
        domNode: HTMLImageElement
      ) {
        CustomImageFormat.constrainIfNoWidth(domNode);

        return allowedImageAttributes.reduce(
          (formats, attribute) => {

            if (domNode.hasAttribute(attribute)) {
              formats[attribute] = domNode.getAttribute(attribute);
            }

            return formats;
          },
          {}
        );
      }

      format(
        name: string,
        value: string
      ) {
        if (allowedImageAttributes.indexOf(name) == -1) {
          super.format(name, value);
        } else {

          if (value?.length) {
            this.domNode.setAttribute(name, value);
          } else {
            this.domNode.removeAttribute(name);
          }
        }

        CustomImageFormat.constrainIfNoWidth(this.domNode);
      }
    }

    Quill.register(CustomImageFormat, true);
  }

}
